/**
 * GlobalKeys — Single source of truth for all global store key names.
 *
 * Usage:
 *   import { GK } from '@/store/GlobalKeys';
 *   GlobalStore.get(GK.COMPANY_GSTIN)
 *   const gstin = useGlobal(GK.COMPANY_GSTIN);
 */

export const GK = Object.freeze({
  // ─── User ────────────────────────────────────────────────────────────────
  USER_ID:           'user.id',
  USER_NAME:         'user.name',           // login username
  USER_DISPLAY_NAME: 'user.displayName',    // full display name
  USER_ROLE:         'user.role',           // SUPER_ADMIN / ADMIN / USER
  USER_GROUP_ID:     'user.groupId',
  USER_GROUP_NAME:   'user.groupName',
  USER_LEVEL:        'user.level',
  USER_TYPE:         'user.type',           // M / S / A
  USER_EMAIL:        'user.email',
  USER_PHONE:        'user.phone',
  USER_IMAGE:        'user.image',          // profile image path
  USER_BRANCH_ID:    'user.branchId',
  USER_BRANCH_NAME:  'user.branchName',

  // ─── Company ─────────────────────────────────────────────────────────────
  COMPANY_ID:        'company.id',          // e.g. "IND20255"
  COMPANY_NAME:      'company.name',
  COMPANY_SHORT:     'company.short',       // SCompName / CompInit
  COMPANY_ADDRESS:   'company.address',
  COMPANY_CITY:      'company.city',
  COMPANY_STATE:     'company.state',
  COMPANY_STATE_CODE:'company.stateCode',
  COMPANY_ZIP:       'company.zip',
  COMPANY_COUNTRY:   'company.country',
  COMPANY_PHONE:     'company.phone',
  COMPANY_EMAIL:     'company.email',
  COMPANY_URL:       'company.url',
  COMPANY_PAN:       'company.pan',         // Income Tax PAN
  COMPANY_GSTIN:     'company.gstin',       // GST registration number
  COMPANY_CST:       'company.cst',
  COMPANY_CURRENCY:  'company.currency',    // "INR" / "USD"
  COMPANY_REG:       'company.reg',

  // ─── Fiscal Year ─────────────────────────────────────────────────────────
  FISCAL_YEAR:       'fy.label',            // e.g. "2025–2026"
  FY_START_DATE:     'fy.start',            // ISO date string
  FY_END_DATE:       'fy.end',
  FY_START_MONTH:    'fy.startMonth',       // 1-12
  FY_END_MONTH:      'fy.endMonth',

  // ─── Branch (active/default) ─────────────────────────────────────────────
  BRANCH_ID:         'branch.id',
  BRANCH_NAME:       'branch.name',
  BRANCH_LOCATION:   'branch.location',
  BRANCH_GSTIN:      'branch.gstin',
  BRANCH_TYPE:       'branch.type',
  BRANCH_WAREHOUSE:  'branch.warehouse',
  BRANCHES_LIST:     'branch.list',         // JSON array of {id, name, ...}

  // ─── Business Date ───────────────────────────────────────────────────────
  BUSINESS_DATE:     'biz.date',            // "YYYY-MM-DD" ISO string (display)
  BUSINESS_DATE_FMT: 'biz.dateFmt',         // "DD-MMM-YYYY" display string
  INT_BUSINESS_DATE: 'biz.intDate',         // HBS integer (for DB queries — use HBSDate.businessDate())
  BIZ_DAY_STATUS:    'biz.dayStatus',       // 'open' | 'closed'

  // ─── HBS Fiscal Year integers ────────────────────────────────────────────
  INT_FY_START:      'fy.intStart',         // HBS integer = StartMonth from S_Company
  INT_FY_END:        'fy.intEnd',           // HBS integer = EndMonth from S_Company

  // ─── License ─────────────────────────────────────────────────────────────
  LICENSE_CLIENT_ID: 'lic.clientId',
  LICENSE_STATUS:    'lic.status',          // 'Live' | 'Trial' | 'Expired'
  LICENSE_EXPIRY:    'lic.expiry',          // ISO date
  LICENSE_DAYS_LEFT: 'lic.daysLeft',
  LICENSE_PACKAGES:  'lic.packages',

  // ─── Globalization (all sourced from DB — never hardcoded) ──────────────
  CURRENCY:          'glob.currency',       // currency code e.g. "76" or resolved "INR"
  CURRENCY_CODE:     'glob.currencyCode',   // ISO code resolved from CurrencyMaster e.g. "INR"
  CURRENCY_NAME:     'glob.currencyName',   // "Indian Rupee"
  CURRENCY_SYMBOL:   'glob.symbol',         // "₹" — resolved from CurrencyMaster.CurrencySymbol
  CURRENCY_DECIMALS: 'glob.decimals',       // 2
  GRID_DECIMAL:      'glob.gridDecimal',    // e.g. "##,##,##0.00" (from Pro_DTO.GridDecimal)
  QTY_DECIMAL:       'glob.qtyDecimal',     // e.g. "##,##,##0.000" (from Pro_DTO.QtyDecimal)
  DATE_FORMAT:       'glob.dateFormat',     // "DD-MMM-YYYY" (strDateFormat from GlobalFunctions_UI)
  DATE_SEPARATOR:    'glob.dateSeparator',  // "-" (strDateSeparaor)
  DECIMAL_FORMAT:    'glob.decimalFormat',  // "##,##,##0.00"
  LOCALE:            'glob.locale',         // "en-IN" — derived from country, never hardcoded
  LANGUAGE:          'glob.language',       // "English"
  COUNTRY:           'glob.country',        // resolved from company CountryId (IND → India)
  COUNTRY_ID:        'glob.countryId',      // raw code e.g. "IND"
  STATE_CODE:        'glob.stateCode',      // e.g. "36" (from S_Company.CompState)
  TIMEZONE:          'glob.timezone',       // derived from country (IND → Asia/Kolkata)

  // ─── System / Session ────────────────────────────────────────────────────
  SESSION_ID:            'sys.sessionId',
  LOGIN_TIME:            'sys.loginTime',            // ISO timestamp — set on bootstrap
  EXIT_TIME:             'sys.exitTime',             // ISO timestamp — set on logout before clear
  SESSION_DURATION_MINS: 'sys.sessionDurationMins',  // integer — calculated on logout
  APP_VERSION:           'sys.version',
  DB_SERVER:             'sys.dbServer',
  BOOTSTRAP_DONE:        'sys.bootstrapDone',        // boolean — all values loaded

  // ─── Tenant (runtime context) ─────────────────────────────────────────────
  TENANT_CLIENT_ID:      'tenant.clientId',          // clientId resolved at login time
  TENANT_COMPANY_ID:     'tenant.companyId',         // compId resolved at login time
  TENANT_LOGIN_TIME:     'tenant.loginTime',         // tenant-scoped login timestamp
  TENANT_DB_NAME:        'tenant.dbName',            // SQLDBName of the tenant DB
});

/** Reverse map: value → key name (useful for debug) */
export const GK_NAMES = Object.fromEntries(
  Object.entries(GK).map(([name, val]) => [val, name])
);

export default GK;
